import { useParams, useNavigate } from 'react-router-dom';
import { useStudio } from '@/features/studio/contexts/StudioContext';
import { useStudioMembers } from '@/features/studio/hooks/useStudio';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { ArrowLeft, User, FileText, CalendarDays } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

export default function MemberProfile() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { membership } = useStudio();
  const { data: members = [] } = useStudioMembers();
  const member = members.find(m => m.user_id === userId);

  const { data: contracts = [] } = useQuery({
    queryKey: ['member-contracts', userId],
    enabled: !!userId && membership?.isOwner,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('signed_contracts')
        .select('id, template_name, client_name, signed_at')
        .eq('owner_id', userId!)
        .order('signed_at', { ascending: false })
        .limit(20);
      if (error) throw error;
      return data || [];
    },
  });

  const { data: appointments = [] } = useQuery({
    queryKey: ['member-appointments', userId],
    enabled: !!userId && membership?.isOwner,
    queryFn: async () => {
      const today = new Date().toISOString().split('T')[0];
      const { data, error } = await supabase
        .from('appointments')
        .select('id, client_name, date, start_time, status, price')
        .eq('owner_id', userId!)
        .gte('date', today)
        .order('date', { ascending: true })
        .limit(10);
      if (error) throw error;
      return data || [];
    },
  });

  if (!membership?.isOwner) {
    return <div className="flex items-center justify-center h-64 text-muted-foreground">Accès réservé au propriétaire.</div>;
  }

  const displayName = member?.display_name ?? member?.email ?? userId?.slice(0, 8) ?? 'Artiste';

  return (
    <div className="tablet-container pb-28 space-y-6">
      <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-4 pt-6">
        <button onClick={() => navigate('/studio')} className="touch-button p-3 bg-secondary rounded-xl">
          <ArrowLeft size={20} />
        </button>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
            <User size={18} className="text-primary" />
          </div>
          <div>
            <h1 className="text-xl font-bold font-display">{displayName}</h1>
            {member && <p className="text-xs text-muted-foreground capitalize">{member.role} · {member.status}</p>}
          </div>
        </div>
      </motion.div>

      <section className="space-y-3">
        <h2 className="font-semibold flex items-center gap-2">
          <FileText size={16} className="text-primary" />
          Contrats récents ({contracts.length})
        </h2>
        {contracts.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">Aucun contrat signé.</p>
        ) : (
          <div className="space-y-2">
            {(contracts as any[]).map(c => (
              <div key={c.id} className="card-elevated p-4">
                <p className="font-medium text-sm">{c.template_name}</p>
                <p className="text-xs text-muted-foreground">
                  {c.client_name} · {format(new Date(c.signed_at), 'd MMM yyyy', { locale: fr })}
                </p>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="space-y-3">
        <h2 className="font-semibold flex items-center gap-2">
          <CalendarDays size={16} className="text-primary" />
          Prochains rendez-vous ({appointments.length})
        </h2>
        {appointments.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">Aucun rendez-vous à venir.</p>
        ) : (
          <div className="space-y-2">
            {(appointments as any[]).map(a => (
              <div key={a.id} className="card-elevated p-4 flex items-center justify-between">
                <div>
                  <p className="font-medium text-sm">{a.client_name}</p>
                  <p className="text-xs text-muted-foreground">
                    {format(new Date(a.date), 'd MMM', { locale: fr })} · {a.start_time.slice(0, 5)}
                  </p>
                </div>
                <span className="text-sm font-semibold text-primary">{a.price > 0 ? `${a.price}€` : '—'}</span>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
